import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { S } from '../utils/styles';
import { formatDate } from '../utils/styles';

const NOTE_COLORS = {
  'Customer Instruction':{ bg:'#eff6ff', color:'#3b82f6', border:'#3b82f6' },
  'Internal Mistake':{ bg:'#fef2f2', color:'#ef4444', border:'#ef4444' },
  'Vendor Issue':{ bg:'#fff7ed', color:'#f97316', border:'#f97316' },
  'Quality Issue':{ bg:'#fefce8', color:'#ca8a04', border:'#ca8a04' },
  'Delay Reason':{ bg:'#fdf4ff', color:'#a855f7', border:'#a855f7' },
  'General':{ bg:'#f0fdf4', color:'#10b981', border:'#10b981' },
};
function noteColor(cat) { return NOTE_COLORS[cat] || { bg:'#f9fafb', color:'#6b7280', border:'#6b7280' }; }

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [category, setCategory] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showCatMgr, setShowCatMgr] = useState(false);
  const [addForm, setAddForm] = useState({ order_id:'', category:'General', note:'' });
  const [newCat, setNewCat] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/customers').then(r => setCustomers(r.data)).catch(()=>{});
    api.get('/orders?status=active').then(r => setOrders(r.data)).catch(()=>{});
    api.get('/inventory/note-categories').then(r => setCategories(r.data)).catch(()=>{});
    load();
  }, []);

  useEffect(() => { load(); }, [category, customerId]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (customerId) params.set('customer_id', customerId);
      const { data } = await api.get(`/orders/notes/all?${params}`);
      setNotes(data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const filtered = search
    ? notes.filter(n => n.note.toLowerCase().includes(search.toLowerCase()) ||
        (n.company_name||'').toLowerCase().includes(search.toLowerCase()) ||
        (n.pi_number||'').toLowerCase().includes(search.toLowerCase()))
    : notes;

  const saveNote = async () => {
    if (!addForm.order_id || !addForm.note) return alert('Select an order and enter a note');
    setSaving(true);
    try {
      await api.post(`/orders/${addForm.order_id}/notes`, { category: addForm.category, note: addForm.note });
      setShowAdd(false); setAddForm({ order_id:'', category:'General', note:'' }); load();
    } catch(e) { alert('Error saving note'); }
    finally { setSaving(false); }
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await api.post('/inventory/note-categories', { name: newCat.trim() });
      const { data } = await api.get('/inventory/note-categories');
      setCategories(data); setNewCat('');
    } catch(e) { alert(e.response?.data?.error || 'Error'); }
  };

  const deleteCategory = async (cat) => {
    if (cat.is_default) return alert('Cannot delete preset categories');
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    try {
      await api.delete(`/inventory/note-categories/${cat.id}`);
      const { data } = await api.get('/inventory/note-categories');
      setCategories(data);
    } catch(e) { alert(e.response?.data?.error || 'Error'); }
  };

  const catNames = categories.map(c => c.name);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:600, margin:0 }}>Notes Log</h1>
          <div style={{ fontSize:13, color:'#6b7280', marginTop:3 }}>All order notes across all customers</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={() => setShowCatMgr(true)} style={S.btnSecondary}>Manage categories</button>
          <button onClick={() => setShowAdd(true)} style={S.btnPrimary}>+ Add note</button>
        </div>
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap', alignItems:'flex-end' }}>
        <div><label style={S.label}>Search notes</label><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Keyword search..." style={{ ...S.input, width:220 }} /></div>
        <div>
          <label style={S.label}>Filter by category</label>
          <select value={category} onChange={e => setCategory(e.target.value)} style={{ ...S.input, width:200 }}>
            <option value="">All categories</option>
            {catNames.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label style={S.label}>Filter by customer</label>
          <select value={customerId} onChange={e => setCustomerId(e.target.value)} style={{ ...S.input, width:200 }}>
            <option value="">All customers</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
        <button onClick={() => { setCategory(''); setCustomerId(''); setSearch(''); }} style={S.btnSecondary}>Clear all</button>
      </div>

      <div style={{ fontSize:12, color:'#9ca3af', marginBottom:12 }}>{filtered.length} note{filtered.length!==1?'s':''} found</div>

      {loading && <div style={{ color:'#9ca3af', padding:20 }}>Loading...</div>}

      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {filtered.map(n => {
          const nc = noteColor(n.category);
          return (
            <div key={n.id} style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, padding:14, borderLeft:`3px solid ${nc.border}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, flexWrap:'wrap', gap:6 }}>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <span style={{ background:nc.bg, color:nc.color, padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:500 }}>{n.category}</span>
                  <Link to={`/orders/${n.order_id}`} style={{ fontSize:12, color:'#3b82f6', textDecoration:'none', fontWeight:500 }}>{n.pi_number}</Link>
                  <span style={{ fontSize:12, color:'#9ca3af' }}>{n.company_name}</span>
                </div>
                <span style={{ fontSize:11, color:'#9ca3af' }}>{formatDate(n.created_at?.split('T')[0])} · {n.user_name||'—'}</span>
              </div>
              <div style={{ fontSize:13, color:'#374151', lineHeight:1.5 }}>
                {search ? n.note.split(new RegExp(`(${search})`, 'gi')).map((part, i) =>
                  part.toLowerCase() === search.toLowerCase()
                    ? <mark key={i} style={{ background:'#fef9c3', padding:'0 2px', borderRadius:2 }}>{part}</mark>
                    : part
                ) : n.note}
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && <div style={{ textAlign:'center', color:'#9ca3af', padding:40 }}>No notes found</div>}
      </div>

      {/* Add Note Modal */}
      {showAdd && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width:520 }}>
            <div style={{ fontSize:16, fontWeight:600, marginBottom:16 }}>Add note</div>
            <div style={{ marginBottom:12 }}>
              <label style={S.label}>Order *</label>
              <select value={addForm.order_id} onChange={e => setAddForm(p=>({...p,order_id:e.target.value}))} style={S.input}>
                <option value="">Select order...</option>
                {orders.map(o => <option key={o.id} value={o.id}>{o.pi_number} — {o.company_name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={S.label}>Category</label>
              <select value={addForm.category} onChange={e => setAddForm(p=>({...p,category:e.target.value}))} style={S.input}>
                {catNames.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={S.label}>Note *</label>
              <textarea value={addForm.note} onChange={e => setAddForm(p=>({...p,note:e.target.value}))}
                style={{ ...S.input, height:100, resize:'vertical' }} placeholder="Enter note..." />
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={saveNote} disabled={saving||!addForm.order_id||!addForm.note} style={S.btnPrimary}>{saving?'Saving...':'Save note'}</button>
              <button onClick={() => setShowAdd(false)} style={S.btnSecondary}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Category Manager Modal */}
      {showCatMgr && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width:400 }}>
            <div style={{ fontSize:16, fontWeight:600, marginBottom:4 }}>Note categories</div>
            <div style={{ fontSize:13, color:'#6b7280', marginBottom:14 }}>Preset categories cannot be deleted. Custom ones can.</div>
            {categories.map(cat => (
              <div key={cat.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid #f3f4f6' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ background: noteColor(cat.name).bg, color: noteColor(cat.name).color, padding:'2px 8px', borderRadius:20, fontSize:11 }}>{cat.name}</span>
                  {cat.is_default ? <span style={{ fontSize:10, color:'#9ca3af' }}>preset</span> : null}
                </div>
                {!cat.is_default && (
                  <button onClick={() => deleteCategory(cat)} style={{ ...S.btnSm, background:'#fef2f2', color:'#ef4444', padding:'3px 8px', fontSize:11 }}>Delete</button>
                )}
              </div>
            ))}
            <div style={{ display:'flex', gap:8, marginTop:14 }}>
              <input value={newCat} onChange={e => setNewCat(e.target.value)} onKeyDown={e => e.key==='Enter'&&addCategory()} placeholder="New category name..." style={{ ...S.input, flex:1 }} />
              <button onClick={addCategory} disabled={!newCat.trim()} style={S.btnPrimary}>Add</button>
            </div>
            <button onClick={() => setShowCatMgr(false)} style={{ ...S.btnSecondary, marginTop:12 }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
